import React, { useEffect, useState } from "react";
import { api, formatErr, fmtMin, fmtLessonWhen } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FileDown, FileText, Loader2, Send } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const today = () => new Date().toLocaleDateString("sv-SE");

async function download(courseId, body, filename) {
  const r = await fetch(`${BACKEND}/api/courses/${courseId}/teacher-letter`, {
    method: "POST", body: JSON.stringify(body),
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("athena_token")}` },
  });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Generazione non riuscita");
  const cd = r.headers.get("Content-Disposition") || "";
  const name = /filename="([^"]+)"/.exec(cd)?.[1] || filename;
  const url = URL.createObjectURL(await r.blob()); const a = document.createElement("a");
  a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
}

export default function TeacherLetterDialog({ course, open, onClose }) {
  const [data, setData] = useState(null);
  const [tid, setTid] = useState("");
  const [rate, setRate] = useState("");
  const [date, setDate] = useState(today());
  const [busy, setBusy] = useState(null);
  const load = () => api.get(`/courses/${course.id}/teacher-letters`).then((r) => { setData(r.data); setTid((t) => t || r.data.teachers[0]?.id || ""); });
  useEffect(() => { if (open) { setData(null); setTid(""); setRate(""); setDate(today()); load(); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const t = data?.teachers.find((x) => x.id === tid);
  const rateNum = Number(String(rate).replace(",", "."));
  const ready = t && rateNum > 0 && date;
  const body = (format) => ({ teacher_id: tid, hourly_rate: rateNum, letter_date: date, format });
  const run = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "send") {
        if (!window.confirm(`Inviare la lettera di incarico in PDF a ${t.email}?`)) { setBusy(null); return; }
        await api.post(`/courses/${course.id}/teacher-letter/send`, body("pdf"));
        toast.success(`Lettera messa in coda per l'invio a ${t.email}`); load();
      } else {
        await download(course.id, body(kind), `Ordine_incarico_${course.code}.${kind}`);
      }
    } catch (e) { toast.error(formatErr(e.response?.data?.detail || e.message)); }
    setBusy(null);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="teacher-letter-dialog">
        <DialogHeader><DialogTitle>Incarico Docente</DialogTitle></DialogHeader>
        {!data ? <div className="py-8 text-center"><Loader2 className="animate-spin inline" /></div> : (
          <div className="space-y-4">
            {data.teachers.length === 0 && <p className="text-sm text-slate-500">Nessuna lezione di questo corso è collegata a un docente dell'archivio. Scegli il docente nel modulo di ogni lezione.</p>}
            {data.unlinked.length > 0 && <p className="text-xs text-amber-700 bg-amber-50 rounded-md p-2" data-testid="letter-unlinked">Docenti non presenti in archivio: {data.unlinked.join(", ")}. Aggiungili in «Docenti» per generare la loro lettera.</p>}
            {data.teachers.length > 0 && (
              <>
                <div>
                  <Label>Docente</Label>
                  <select value={tid} onChange={(e) => setTid(e.target.value)} className="w-full h-10 rounded-md border border-input px-3 text-sm" data-testid="letter-teacher-select">
                    {data.teachers.map((x) => <option key={x.id} value={x.id}>{x.name} — {x.lessons.length} lezioni, {fmtMin(x.total_minutes)}</option>)}
                  </select>
                </div>
                {t && (
                  <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 text-sm max-h-40 overflow-y-auto" data-testid="letter-lessons">
                    {t.lessons.map((l) => <div key={l.id} className="px-3 py-1.5 flex justify-between gap-3"><span>L{l.number} · {l.title}</span><span className="text-slate-500 whitespace-nowrap">{fmtLessonWhen(l)}</span></div>)}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Compenso orario (€)</Label><Input inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="es. 50,00" data-testid="letter-rate" /></div>
                  <div><Label>Data della lettera</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="letter-date" /></div>
                </div>
                {t && !t.email && <p className="text-xs text-rose-600">Il docente non ha un'e-mail in archivio: puoi scaricare la lettera ma non inviarla.</p>}
              </>
            )}
            {data.history.length > 0 && (
              <div className="text-xs text-slate-500" data-testid="letter-history">
                <div className="font-semibold text-slate-700 mb-1">Lettere inviate</div>
                {data.history.slice(0, 5).map((h) => <div key={h.id}>{new Date(h.at).toLocaleString("it-IT")} · {h.teacher_name} → {h.recipient} · € {h.hourly_rate.toFixed(2).replace(".", ",")}/h</div>)}
              </div>
            )}
          </div>
        )}
        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="outline" disabled={!ready || !!busy} onClick={() => run("docx")} data-testid="letter-docx-btn">{busy === "docx" ? <Loader2 size={14} className="mr-1 animate-spin" /> : <FileText size={14} className="mr-1" />} Scarica Word</Button>
          <Button variant="outline" disabled={!ready || !!busy} onClick={() => run("pdf")} data-testid="letter-pdf-btn">{busy === "pdf" ? <Loader2 size={14} className="mr-1 animate-spin" /> : <FileDown size={14} className="mr-1" />} Scarica PDF</Button>
          <Button disabled={!ready || !t?.email || !!busy} onClick={() => run("send")} className="bg-[#0B192C] hover:bg-[#07111E]" data-testid="letter-send-btn">{busy === "send" ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Send size={14} className="mr-1" />} Invia PDF al docente</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
